import { Route, Routes } from 'react-router-dom';
import { AppShell } from './layout/AppShell';
import { RequireAuth } from './modules/auth/RequireAuth';
import { ShopChooserGate } from './modules/auth/ShopChooserGate';
import { RequirePermission } from './modules/auth/RequirePermission';
import { LoginPage } from './modules/auth/LoginPage';
import { AutoLanding } from './modules/auth/AutoLanding';
import { VerifyDocumentPage } from './modules/verify/VerifyDocumentPage';

import { CataloguePage } from './modules/sales/CataloguePage';
import { CatalogueDetailPage } from './modules/sales/CatalogueDetailPage';
import { NewOrderPage } from './modules/sales/NewOrderPage';
import { LegacyIntakePage } from './modules/sales/LegacyIntakePage';
import { OrdersListPage } from './modules/sales/OrdersListPage';
import { OrderDetailPage } from './modules/sales/OrderDetailPage';
import { DiscountApprovalsPage } from './modules/sales/DiscountApprovalsPage';
import { CustomersPage } from './modules/sales/CustomersPage';
import { CustomerDetailPage } from './modules/sales/CustomerDetailPage';
import { StockPage } from './modules/sales/StockPage';
import { UnitNumbersPage } from './modules/sales/UnitNumbersPage';
import { ReservationsPage } from './modules/sales/ReservationsPage';
import { CashClosePage } from './modules/sales/CashClosePage';
import { ShopHandoverPage } from './modules/sales/ShopHandoverPage';
import { QuotationsListPage } from './modules/sales/quotations/QuotationsListPage';
import { NewQuotationPage } from './modules/sales/quotations/NewQuotationPage';
import { QuotationDetailPage } from './modules/sales/quotations/QuotationDetailPage';

import { FinanceDashboard } from './modules/finance/FinanceDashboard';
import { ReportsPage } from './modules/finance/ReportsPage';
import { SalesSheetPage } from './modules/finance/SalesSheetPage';
import { ReceivablesPage } from './modules/finance/ReceivablesPage';
import { CashFlowPage } from './modules/finance/CashFlowPage';
import { VatPage } from './modules/finance/VatPage';
import { CommissionPage } from './modules/finance/CommissionPage';
import { MyCommissionPage } from './modules/sales/MyCommissionPage';
import { ManufacturingPage } from './modules/operations/ManufacturingPage';
import { SupplierPage } from './modules/operations/SupplierPage';
import { AwaitingImportPage } from './modules/operations/AwaitingImportPage';
import { DeliveryReadyPage } from './modules/operations/DeliveryReadyPage';
import { CreditNotesPage } from './modules/finance/credit-notes/CreditNotesPage';
import { CreditNoteDetailPage } from './modules/finance/credit-notes/CreditNoteDetailPage';
import { IssueCreditNotePage } from './modules/finance/credit-notes/IssueCreditNotePage';
import { DeliveryNotesPage } from './modules/sales/delivery-notes/DeliveryNotesPage';
import { DeliveryNoteDetailPage } from './modules/sales/delivery-notes/DeliveryNoteDetailPage';
import { NewDeliveryNotePage } from './modules/sales/delivery-notes/NewDeliveryNotePage';
import { DriverRemittancesPage } from './modules/finance/driver-remittances/DriverRemittancesPage';
import { RemittanceDetailPage } from './modules/finance/driver-remittances/RemittanceDetailPage';
import { NewRemittancePage } from './modules/finance/driver-remittances/NewRemittancePage';
import { BankDepositsPage } from './modules/finance/bank-deposits/BankDepositsPage';
import { PettyCashPage } from './modules/finance/petty-cash/PettyCashPage';
import { PaymentsPendingPage } from './modules/finance/PaymentsPendingPage';
import { JournalVouchersPage } from './modules/finance/journal-vouchers/JournalVouchersPage';

import { ShopsPage } from './modules/admin/ShopsPage';
import { DevicesPage } from './modules/admin/DevicesPage';
import { UsersPage } from './modules/admin/UsersPage';
import { CategoriesPage } from './modules/admin/CategoriesPage';
import { CommissionSettingsPage } from './modules/admin/CommissionSettingsPage';
import { ProductsPage } from './modules/admin/ProductsPage';
import { SettingsPage } from './modules/admin/SettingsPage';
import { AuditLogPage } from './modules/system/AuditLogPage';

import { NotFoundPage } from './modules/common/NotFoundPage';

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public — document authenticity check reached by scanning a QR code */}
      <Route path="/verify/:docType/:id" element={<VerifyDocumentPage />} />

      <Route
        element={
          <RequireAuth>
            <ShopChooserGate>
              <AppShell />
            </ShopChooserGate>
          </RequireAuth>
        }
      >
        <Route path="/" element={<AutoLanding />} />
        <Route
          path="/catalogue"
          element={
            <RequirePermission permission="products.view">
              <CataloguePage />
            </RequirePermission>
          }
        />
        <Route
          path="/catalogue/:id"
          element={
            <RequirePermission permission="products.view">
              <CatalogueDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/orders"
          element={
            <RequirePermission permissions={['orders.create', 'orders.view_all_shops']} any>
              <OrdersListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/orders/legacy-intake"
          element={
            <RequirePermission permission="orders.legacy_intake">
              <LegacyIntakePage />
            </RequirePermission>
          }
        />
        <Route
          path="/orders/new"
          element={
            <RequirePermission permission="orders.create">
              <NewOrderPage />
            </RequirePermission>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <RequirePermission permissions={['orders.create', 'orders.view_all_shops']} any>
              <OrderDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/discount-approvals"
          element={
            <RequirePermission permissions={['discounts.apply_above_ceiling', 'orders.create']} any>
              <DiscountApprovalsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/customers"
          element={
            <RequirePermission permission="customers.create">
              <CustomersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/customers/:id"
          element={
            <RequirePermission permission="customers.create">
              <CustomerDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/quotations"
          element={
            <RequirePermission permission="quotations.view">
              <QuotationsListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/quotations/new"
          element={
            <RequirePermission permission="quotations.create">
              <NewQuotationPage />
            </RequirePermission>
          }
        />
        <Route
          path="/quotations/:id"
          element={
            <RequirePermission permission="quotations.view">
              <QuotationDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/stock"
          element={
            <RequirePermission permission="stock.view">
              <StockPage />
            </RequirePermission>
          }
        />
        <Route
          path="/stock/units"
          element={
            <RequirePermission permission="stock.view">
              <UnitNumbersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reservations"
          element={
            <RequirePermission permissions={['orders.create', 'orders.view_all_shops']} any>
              <ReservationsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/cash-close"
          element={
            <RequirePermission permissions={['cash.close', 'orders.view_all_shops']} any>
              <CashClosePage />
            </RequirePermission>
          }
        />
        <Route
          path="/cash-handover"
          element={
            <RequirePermission permission="remittances.submit_drawer">
              <ShopHandoverPage />
            </RequirePermission>
          }
        />

        <Route
          path="/finance"
          element={
            <RequirePermission permission="finance.view_dashboard">
              <FinanceDashboard />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/sales-sheet"
          element={
            <RequirePermission permission="finance.view_dashboard">
              <SalesSheetPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/reports"
          element={
            <RequirePermission permission="finance.view_dashboard">
              <ReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/receivables"
          element={
            <RequirePermission permission="finance.view_receivables">
              <ReceivablesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/cash-flow"
          element={
            <RequirePermission permission="cash.view_flow">
              <CashFlowPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/vat"
          element={
            <RequirePermission permission="finance.view_vat">
              <VatPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/commission"
          element={
            <RequirePermission
              permissions={['finance.view_commission', 'finance.view_all_commissions']}
              any
            >
              <CommissionPage />
            </RequirePermission>
          }
        />
        <Route
          path="/my-commission"
          element={
            <RequirePermission permission="finance.view_commission">
              <MyCommissionPage />
            </RequirePermission>
          }
        />
        <Route
          path="/operations/manufacturing"
          element={
            <RequirePermission permission="operations.view">
              <ManufacturingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/operations/suppliers"
          element={
            <RequirePermission permission="operations.view">
              <SupplierPage />
            </RequirePermission>
          }
        />
        <Route
          path="/operations/imports"
          element={
            <RequirePermission permission="operations.view">
              <AwaitingImportPage />
            </RequirePermission>
          }
        />
        <Route
          path="/operations/delivery"
          element={
            <RequirePermission permission="operations.view">
              <DeliveryReadyPage />
            </RequirePermission>
          }
        />

        <Route
          path="/credits"
          element={
            <RequirePermission permission="credit_notes.create">
              <CreditNotesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/credits/new"
          element={
            <RequirePermission permission="credit_notes.create">
              <IssueCreditNotePage />
            </RequirePermission>
          }
        />
        <Route
          path="/credits/:id"
          element={
            <RequirePermission permission="credit_notes.create">
              <CreditNoteDetailPage />
            </RequirePermission>
          }
        />

        <Route
          path="/delivery-notes"
          element={
            <RequirePermission permission="delivery_notes.create">
              <DeliveryNotesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/delivery-notes/new"
          element={
            <RequirePermission permission="delivery_notes.create">
              <NewDeliveryNotePage />
            </RequirePermission>
          }
        />
        <Route
          path="/delivery-notes/:id"
          element={
            <RequirePermission permission="delivery_notes.create">
              <DeliveryNoteDetailPage />
            </RequirePermission>
          }
        />

        <Route
          path="/remittances"
          element={
            <RequirePermission
              permissions={[
                'remittances.submit',
                'remittances.submit_drawer',
                'remittances.acknowledge',
              ]}
              any
            >
              <DriverRemittancesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/remittances/new"
          element={
            <RequirePermission permission="remittances.submit">
              <NewRemittancePage />
            </RequirePermission>
          }
        />
        <Route
          path="/remittances/:id"
          element={
            <RequirePermission
              permissions={[
                'remittances.submit',
                'remittances.submit_drawer',
                'remittances.acknowledge',
              ]}
              any
            >
              <RemittanceDetailPage />
            </RequirePermission>
          }
        />

        <Route
          path="/bank-deposits"
          element={
            <RequirePermission permission="deposits.create">
              <BankDepositsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/payments-pending"
          element={
            <RequirePermission permission="payments.verify">
              <PaymentsPendingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/petty-cash"
          element={
            <RequirePermission permission="petty_cash.create">
              <PettyCashPage />
            </RequirePermission>
          }
        />
        <Route
          path="/journal-vouchers"
          element={
            <RequirePermission permission="journal_vouchers.create">
              <JournalVouchersPage />
            </RequirePermission>
          }
        />

        <Route
          path="/admin/shops"
          element={
            <RequirePermission permission="shops.manage">
              <ShopsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/devices"
          element={
            <RequirePermission permission="manage_devices">
              <DevicesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequirePermission permission="users.manage">
              <UsersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <RequirePermission permission="categories.manage">
              <CategoriesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/products"
          element={
            <RequirePermission permission="products.create">
              <ProductsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/commissions"
          element={
            <RequirePermission permission="commissions.manage">
              <CommissionSettingsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RequirePermission permission="shops.manage">
              <SettingsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <RequirePermission permission="system.view_audit_log">
              <AuditLogPage />
            </RequirePermission>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
